import { z } from "zod";

export const GrowthPlatformSchema = z.enum(["tiktok_shop", "temu", "shopee"]);
export type GrowthPlatform = z.infer<typeof GrowthPlatformSchema>;
/**
 * 成长路线阶段。
 *
 * `product_feasibility` / `supplier_profit` / `listing_preparation` 已被
 * `product_launch`（选品到上架）合并替代，**只保留在迁移与历史数据读取边界**，
 * 不再用于创建新路线或驱动新界面。保留它们是为了让旧记录仍能被解析出来，
 * 而不是给新代码多一个可选项。
 */
export const GrowthStageCodeSchema = z.enum([
  "business_assessment", "platform_selection", "store_registration",
  "product_launch",
  "product_feasibility", "supplier_profit", "listing_preparation",
  "fulfillment_service", "growth_operations",
  "independent_operation", "competency_review",
]);
export type GrowthStageCode = z.infer<typeof GrowthStageCodeSchema>;
export const GrowthTaskKindSchema = z.enum([
  "business_assessment", "platform_selection", "store_registration",
  "product_launch",
  "product_feasibility", "supplier_profit", "listing_preparation",
  "fulfillment_service", "growth_operations",
  "independent_review",
]);
export type GrowthTaskKind = z.infer<typeof GrowthTaskKindSchema>;

/** 统一商品阶段的 kind / stage。路线阶段与商品执行任务是一对一关系。 */
export const PRODUCT_LAUNCH_KIND = "product_launch" as const;
export const PRODUCT_LAUNCH_STAGE_TITLE = "选品到上架";
/** 只用于迁移与历史读取；不创建、不驱动新界面。 */
export const LEGACY_PRODUCT_LAUNCH_KINDS = ["product_feasibility", "supplier_profit", "listing_preparation"] as const;
export const isLegacyProductLaunchKind = (kind: GrowthTaskKind): boolean =>
  (LEGACY_PRODUCT_LAUNCH_KINDS as readonly string[]).includes(kind);
/** 与商品上架有关的 kind：统一阶段本身，或迁移前的三个阶段之一。 */
export const isProductLaunchKind = (kind: GrowthTaskKind): boolean =>
  kind === PRODUCT_LAUNCH_KIND || isLegacyProductLaunchKind(kind);
export const GrowthTaskStatusSchema = z.enum(["queued", "active", "waiting_user", "waiting_external", "ready_for_review", "passed", "needs_revision", "cancelled"]);
export type GrowthTaskStatus = z.infer<typeof GrowthTaskStatusSchema>;
export const CompetencyLevelSchema = z.enum(["L0", "L1", "L2", "L3"]);
export type CompetencyLevel = z.infer<typeof CompetencyLevelSchema>;
export const CompetencyCodeSchema = z.enum([
  "business_strategy", "platform_operation", "store_setup", "product_selection", "profit_management",
  "supplier_management", "listing_operation", "risk_compliance", "independent_execution",
  "fulfillment_service", "customer_service", "traffic_growth",
]);
export type CompetencyCode = z.infer<typeof CompetencyCodeSchema>;

export const BusinessProfileSchema = z.object({
  platforms: z.array(z.string().min(1).max(60)).max(12).default([]),
  selectedPlatform: GrowthPlatformSchema.nullable(),
  targetMarkets: z.array(z.string().min(1).max(60)).max(12),
  countries: z.array(z.string().min(1).max(60)).max(24),
  categories: z.array(z.string().min(1).max(100)).max(20),
  experienceStage: z.enum(["preparing", "beginner", "operating", "scaling"]).nullable(),
  businessType: z.enum(["individual", "sole_proprietor", "company"]).nullable(),
  budgetCents: z.number().int().nonnegative().nullable(),
  weeklyHours: z.number().int().min(1).max(168).nullable(),
  goals: z.array(z.string().min(1).max(240)).max(12),
  constraints: z.array(z.string().min(1).max(240)).max(12),
  diagnosticCompleted: z.boolean(),
  updatedAt: z.string(),
});
export type BusinessProfile = z.infer<typeof BusinessProfileSchema>;

export const UpsertBusinessProfileRequestSchema = BusinessProfileSchema.omit({ updatedAt: true, diagnosticCompleted: true }).partial().extend({
  platforms: z.array(z.string().min(1).max(60)).max(12).optional(),
});
export type UpsertBusinessProfileRequest = z.infer<typeof UpsertBusinessProfileRequestSchema>;

export const GrowthTaskSummarySchema = z.object({
  id: z.string().uuid(),
  kind: GrowthTaskKindSchema,
  stageCode: GrowthStageCodeSchema,
  title: z.string().min(1).max(180),
  status: GrowthTaskStatusSchema,
  position: z.number().int().positive(),
  nextAction: z.string().min(1).max(500),
  evidenceTaskIds: z.array(z.string().max(120)).max(30),
  completedAt: z.string().nullable(),
  completionSource: z.enum(["user_confirmed", "profile_inferred", "task_result"]).optional(),
});
export type GrowthTaskSummary = z.infer<typeof GrowthTaskSummarySchema>;

export const GrowthRouteSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["active", "completed"]),
  platform: GrowthPlatformSchema.nullable(),
  currentStageCode: GrowthStageCodeSchema,
  completedTaskCount: z.number().int().nonnegative(),
  totalTaskCount: z.number().int().positive(),
  tasks: z.array(GrowthTaskSummarySchema),
  competencies: z.array(z.object({ code: CompetencyCodeSchema, level: CompetencyLevelSchema, evidenceCount: z.number().int().nonnegative() })),
  createdAt: z.string(),
  updatedAt: z.string(),
}).superRefine((value, context) => {
  if (value.totalTaskCount !== value.tasks.length || value.completedTaskCount > value.totalTaskCount) {
    context.addIssue({ code: "custom", message: "路线进度与任务不一致" });
  }
});
export type GrowthRoute = z.infer<typeof GrowthRouteSchema>;

export const CreateGrowthRouteRequestSchema = z.object({
  platform: GrowthPlatformSchema.optional(),
  goal: z.string().trim().max(300).optional(),
});
export type CreateGrowthRouteRequest = z.infer<typeof CreateGrowthRouteRequestSchema>;

export const ConfirmGrowthAssessmentRequestSchema = z.object({ profileUpdatedAt: z.iso.datetime({ offset: true }) });
export const SelectGrowthPlatformRequestSchema = z.object({
  platform: GrowthPlatformSchema,
  confirmed: z.boolean().optional(),
  profileUpdatedAt: z.iso.datetime({ offset: true }).optional(),
}).superRefine((value, context) => {
  if (value.confirmed && !value.profileUpdatedAt) context.addIssue({ code: "custom", path: ["profileUpdatedAt"], message: "确认平台前请提供最新资料版本" });
});
export const LinkGrowthEvidenceRequestSchema = z.object({ taskId: z.string().min(1).max(120) });
